import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { ClausesSelect } from '~/components/Shared/ClausesSelect/ClausesSelect';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { Section } from '~/eds/src/components/Section/Section';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { Nullable } from '~/eds/src/types/Nullable';
import { Option } from '~/eds/src/types/Option';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

type RadioGroupType = Nullable<'add' | 'delete'>;
export type EditClausesData = {
  actionType: RadioGroupType;
  isAllClausesSelected: boolean;
  clausesNames?: string[];
  clausesNewValues?: Record<string, string>;
};
interface Props {
  onChange: (params: EditClausesData) => void;
}

const getRadioGroupOptions = (
  isAllClausesSelected: boolean,
  shouldIncludeAddOption: boolean,
  intl: IntlShape,
): Option<RadioGroupType>[] => [
  ...((shouldIncludeAddOption
    ? [
        {
          label: intl.formatMessage({
            id: 'EditClausesAction.addToExistingValues',
            defaultMessage: 'Add to existing values',
            description: 'Radio option to add to existing clause values',
          }),
          info: intl.formatMessage({
            id: 'EditClausesAction.addToExistingInfo',
            defaultMessage:
              'This will add to the existing clause value. This cannot be undone.',
            description: 'Information text for add to existing values option',
          }),
          tooltip: isAllClausesSelected
            ? intl.formatMessage({
                id: 'EditClausesAction.addToExistingTooltip',
                defaultMessage:
                  'This function is not available when you select "All document clauses". Select specific clauses to add additional values to.',
                description:
                  'Tooltip explaining why add option is disabled for all clauses',
              })
            : undefined,
          value: 'add',
          disabled: isAllClausesSelected,
        },
      ]
    : []) as Option<RadioGroupType>[]),
  {
    label: intl.formatMessage({
      id: 'EditClausesAction.deleteExistingValues',
      defaultMessage: 'Delete existing values',
      description: 'Radio option to delete existing clause values',
    }),
    info: intl.formatMessage({
      id: 'EditClausesAction.deleteExistingInfo',
      defaultMessage:
        'This will remove the current clause value. This cannot be undone.',
      description: 'Information text for delete existing values option',
    }),
    value: 'delete',
  },
];

export const EditClausesAction = ({ onChange }: Props) => {
  const intl = useIntl();
  const isAdvancedBulkClauseEditingEnabled = useFlag(
    FlagType.AdvancedBulkClauseEditing,
  );

  const [clausesNames, setClauseNames] = React.useState<string[]>([]);
  const [radioGroupValue, setRadioGroupValue] =
    React.useState<Nullable<RadioGroupType>>(null);
  const [clauseValuesRecord, setClauseValuesRecord] = React.useState<
    Record<string, string> | undefined
  >();

  const isAllClausesSelected = useMemo(
    () => clausesNames?.some((value) => value === 'select_all'),
    [clausesNames],
  );

  const selectAllOption = {
    label: intl.formatMessage({
      id: 'EditClausesAction.selectAllDocumentsClauses',
      defaultMessage: 'Select all documents clauses',
      description: 'Option label to select all document clauses',
    }),
    value: 'select_all',
  };
  const onSelectChange = (updatedValue: Nullable<string[]>) => {
    if (updatedValue) {
      setClauseNames(updatedValue);
      if (radioGroupValue === 'add') {
        updateClauseValuesRecord(updatedValue);
      }
    }
  };
  const updateClauseValuesRecord = (selectedClauses: string[]) => {
    const filteredClauses = selectedClauses.filter(
      (clause) => clause !== 'select_all',
    );
    if (filteredClauses.length) {
      const updatedRecords: typeof clauseValuesRecord = selectedClauses.reduce(
        (acc, clauseName) => ({
          ...acc,
          [clauseName]: clauseValuesRecord?.[clauseName] ?? '',
        }),
        {},
      );
      setClauseValuesRecord(updatedRecords);
    }
  };

  useEffect(() => {
    if (radioGroupValue === 'add') {
      setRadioGroupValue(null);
    }
  }, [isAllClausesSelected]);

  useEffect(() => {
    onChange({
      isAllClausesSelected,
      clausesNames: clausesNames.filter((value) => value !== 'select_all'),
      actionType: radioGroupValue,
      clausesNewValues: clauseValuesRecord,
    });
  }, [clausesNames, radioGroupValue, isAllClausesSelected, clauseValuesRecord]);

  const onRadioGroupChange = (updatedValue: Nullable<RadioGroupType>) => {
    setRadioGroupValue(updatedValue);
    if (updatedValue === 'add') {
      updateClauseValuesRecord(clausesNames);
    }
  };

  const handleClauseValue = (clauseName: string, clauseValue: string) => {
    setClauseValuesRecord({ ...clauseValuesRecord, [clauseName]: clauseValue });
  };

  const onTextAreaChange = (
    clauseName: string,
    updatedValue: string | null | undefined,
  ) => {
    if (updatedValue !== null) {
      // Remove leading spaces only but allow spaces elsewhere
      const trimmedLeadingSpaces = updatedValue?.replace(/^\s+/, '') ?? '';
      handleClauseValue(clauseName, trimmedLeadingSpaces);
    } else {
      // If updatedValue is null (which shouldn't typically happen in a textarea), treat as empty string
      handleClauseValue(clauseName, '');
    }
  };

  return (
    <Layout direction="column" spacing={4}>
      <FormField<string, true>
        description={intl.formatMessage({
          id: 'EditClausesAction.description',
          defaultMessage: 'Select the clause(s) you want to edit',
          description: 'Description for clause selection field',
        })}
        input={ClausesSelect}
        inputProps={{
          enableSorting: false,
          enableSelectAll: false,
          enableValueContainer: true,
          isClearable: false,
          isMulti: true,
          isSearchable: true,
          extraOptions:
            isAdvancedBulkClauseEditingEnabled && radioGroupValue !== 'add'
              ? [selectAllOption]
              : undefined,
        }}
        name="clause-select"
        label={intl.formatMessage({
          id: 'EditClausesAction.clausesLabel',
          defaultMessage: 'Clauses',
          description: 'Label for clauses selection field',
        })}
        onChange={onSelectChange}
        value={clausesNames}
      />

      <RadioGroup
        name="deleteReason"
        options={getRadioGroupOptions(
          isAllClausesSelected,
          isAdvancedBulkClauseEditingEnabled,
          intl,
        )}
        value={radioGroupValue}
        onChange={onRadioGroupChange}
      />

      {clausesNames.length > 0 && radioGroupValue === 'add' && (
        <Layout direction="column">
          <Section
            heading="h4"
            title={intl.formatMessage({
              id: 'EditClausesAction.additionalClauseValues',
              defaultMessage: 'Additional clause values',
              description: 'Section title for additional clause values',
            })}
          >
            <Layout direction="column" spacing={4}>
              {clausesNames.map((clause) => (
                <FormField<string, false>
                  key={clause}
                  input={TextArea}
                  inputProps={{
                    rows: 5,
                  }}
                  name={`${clause}-textarea`}
                  label={`${clause}`}
                  onChange={(updatedValue) =>
                    onTextAreaChange(clause, updatedValue)
                  }
                  placeholder={intl.formatMessage({
                    id: 'EditClausesAction.placeholder',
                    defaultMessage: 'Enter new wording for this clause',
                    description: 'Placeholder text for clause value input',
                  })}
                  value={clauseValuesRecord?.[clause] ?? ''}
                />
              ))}
            </Layout>
          </Section>
        </Layout>
      )}
    </Layout>
  );
};
